/**
 * Koniki-NPC w stajni — idle/walk placeholder + karmienie z ręki + parskanie.
 * PLAN-V2 §2.5 (horse_idle/walkA/walkB/eat/nod) + §6 Stable.
 */
import Phaser from 'phaser';
import {
  petNpc,
  tickNpc,
  type NpcBounds,
  type NpcState,
} from '@pieskowo/shared';
import { MIN_TOUCH } from '../../core/UiKit.js';
import type { InteractTarget } from '../../interact/types.js';
import { playFx } from '../../interact/fx.js';
import {
  HORSE_CATALOG,
  horseCatalog,
  type HorseCatalogEntry,
} from './objects.js';

export type HorseDef = HorseCatalogEntry;
export { horseCatalog };

const HORSE_DEFS: readonly HorseDef[] = HORSE_CATALOG;

type AnimPose = 'idle' | 'walkA' | 'walkB' | 'eat' | 'nod';

type HorseVisual = {
  def: HorseDef;
  state: NpcState;
  root: Phaser.GameObjects.Container;
  body: Phaser.GameObjects.Ellipse;
  head: Phaser.GameObjects.Arc;
  mane: Phaser.GameObjects.Arc;
  label: Phaser.GameObjects.Text;
  heart?: Phaser.GameObjects.Text;
  pose: AnimPose;
  walkPhase: number;
  color: number;
  fed: boolean;
};

/** Jeden koń jako InteractTarget (give siana z ręki). */
export class HorseNpc implements InteractTarget {
  x: number;
  y: number;
  isMoving = false;
  readonly id: string;
  readonly namePl: string;
  readonly snapZoneId: string;
  readonly body: Phaser.GameObjects.Container;
  private readonly scene: Phaser.Scene;
  private visual: HorseVisual;
  private onFed?: (horseId: string) => void;

  constructor(
    scene: Phaser.Scene,
    visual: HorseVisual,
    onFed?: (horseId: string) => void,
  ) {
    this.scene = scene;
    this.visual = visual;
    this.id = visual.def.id;
    this.namePl = visual.def.namePl;
    this.snapZoneId = visual.def.snapZoneId;
    this.x = visual.state.x;
    this.y = visual.state.y;
    this.body = visual.root;
    this.onFed = onFed;
  }

  getPosition(): { x: number; y: number } {
    return { x: this.x, y: this.y };
  }

  moveTo(_x: number, _y: number): void {
    /* koń wędruje sam tickNpc */
  }

  holdItem(_textureKey: string | null): void {
    /* NPC nie trzyma w pyszczku gracza */
  }

  async playAction(a: string): Promise<void> {
    if (a === 'eat' || a === 'happy') {
      this.eatAndSnort();
      this.onFed?.(this.id);
    } else {
      this.nod();
    }
    return new Promise((resolve) => {
      this.scene.time.delayedCall(400, () => resolve());
    });
  }

  /** Jedzenie z ręki + parskanie (PLAN: koń je + parska). */
  eatAndSnort(): void {
    this.visual.pose = 'eat';
    this.visual.fed = true;
    this.visual.state = {
      ...petNpc(this.visual.state),
      emote: 'yum',
      phase: 'react',
      phaseUntil: Date.now() + 800,
    };
    this.scene.tweens.add({
      targets: this.visual.root,
      scaleX: 1.12,
      scaleY: 0.95,
      duration: 160,
      yoyo: true,
      repeat: 2,
    });
    playFx(this.scene, 'crumbs', this.x, this.y - 30);
    playFx(this.scene, 'hearts', this.x, this.y - 50);
    this.showBubble('😋 prrr!');
    // dźwięk proceduralny parskania
    playFx(this.scene, 'notes', this.x + 20, this.y - 20);
  }

  brush(): void {
    this.visual.pose = 'nod';
    playFx(this.scene, 'hearts', this.x, this.y - 40);
    this.showBubble('💖');
    this.scene.tweens.add({
      targets: this.visual.root,
      angle: 4,
      duration: 120,
      yoyo: true,
      repeat: 3,
      onComplete: () => {
        this.visual.root.setAngle(0);
        this.visual.pose = 'idle';
      },
    });
  }

  nod(): void {
    this.visual.pose = 'nod';
    this.scene.tweens.add({
      targets: this.visual.head,
      y: this.visual.head.y + 6,
      duration: 180,
      yoyo: true,
      onComplete: () => {
        this.visual.pose = 'idle';
      },
    });
  }

  private showBubble(emoji: string): void {
    this.visual.heart?.destroy();
    this.visual.heart = this.scene.add
      .text(0, -56, emoji, { fontSize: '28px' })
      .setOrigin(0.5);
    this.visual.root.add(this.visual.heart);
    this.scene.tweens.add({
      targets: this.visual.heart,
      y: -90,
      alpha: 0,
      duration: 900,
      onComplete: () => {
        this.visual.heart?.destroy();
        this.visual.heart = undefined;
      },
    });
  }

  syncFromVisual(): void {
    this.x = this.visual.state.x;
    this.y = this.visual.state.y;
  }

  get fed(): boolean {
    return this.visual.fed;
  }
}

export class StableHorses {
  private horses: HorseVisual[] = [];
  private targets: HorseNpc[] = [];
  private bounds: NpcBounds = { minX: 780, minY: 320, maxX: 1320, maxY: 520 };
  private hayBusyUntil = 0;
  private onFed?: (horseId: string) => void;
  private onPetApi?: (horseId: string) => void;

  constructor(
    private readonly scene: Phaser.Scene,
    opts?: {
      onPetApi?: (horseId: string) => void;
      onFed?: (horseId: string) => void;
    },
  ) {
    this.onPetApi = opts?.onPetApi;
    this.onFed = opts?.onFed;
  }

  setBounds(b: NpcBounds): void {
    this.bounds = b;
  }

  /** Give targets dla ObjectLayer. */
  getGiveTargets(): InteractTarget[] {
    return this.targets;
  }

  getHorseNpcs(): HorseNpc[] {
    return this.targets;
  }

  getPositions(): { id: string; x: number; y: number; snapZoneId: string }[] {
    return this.horses.map((h) => ({
      id: h.def.id,
      x: h.state.x,
      y: h.state.y,
      snapZoneId: h.def.snapZoneId,
    }));
  }

  fedIds(): string[] {
    return this.horses.filter((h) => h.fed).map((h) => h.def.id);
  }

  spawn(): void {
    this.destroy();
    for (const h of HORSE_DEFS) {
      const state: NpcState = {
        id: h.id,
        x: h.x,
        y: h.y,
        phase: 'idle',
        phaseUntil: Date.now() + 400 + Math.random() * 1200,
        targetX: h.x,
        targetY: h.y,
        emote: '',
      };

      const body = this.scene.add
        .ellipse(0, 0, 78, 56, h.color)
        .setStrokeStyle(3, 0xffffff);
      const head = this.scene.add
        .circle(40, -10, 20, h.color)
        .setStrokeStyle(2, 0xffffff);
      const mane = this.scene.add.circle(30, -24, 12, 0x5d4037);
      const eye = this.scene.add.circle(46, -12, 3, 0x212121);
      const legL = this.scene.add.rectangle(-18, 28, 10, 22, 0x5d4037);
      const legR = this.scene.add.rectangle(14, 28, 10, 22, 0x5d4037);
      const label = this.scene.add
        .text(0, 42, h.namePl, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '16px',
          color: '#4a3f55',
          fontStyle: 'bold',
          backgroundColor: '#ffffffaa',
          padding: { x: 4, y: 2 },
        })
        .setOrigin(0.5);

      const hit = this.scene.add
        .circle(0, 0, Math.max(MIN_TOUCH / 2, 42), 0xffffff, 0.001)
        .setInteractive({ useHandCursor: true });

      const root = this.scene.add.container(state.x, state.y, [
        body,
        head,
        mane,
        eye,
        legL,
        legR,
        label,
        hit,
      ]);
      root.setSize(MIN_TOUCH + 20, MIN_TOUCH + 20);
      root.setDepth(state.y + 2);

      const visual: HorseVisual = {
        def: h,
        state,
        root,
        body,
        head,
        mane,
        label,
        pose: 'idle',
        walkPhase: 0,
        color: h.color,
        fed: false,
      };
      hit.on('pointerdown', (p: Phaser.Input.Pointer) => {
        p.event?.stopPropagation?.();
        this.pet(visual);
      });
      this.horses.push(visual);
      this.targets.push(new HorseNpc(this.scene, visual, this.onFed));
    }
  }

  /** Tap na siano (legacy) → najbliższy koń je. */
  feedHay(wx: number, wy: number): void {
    const now = Date.now();
    if (now < this.hayBusyUntil) return;
    this.hayBusyUntil = now + 900;
    const nearest = this.nearest(wx, wy);
    if (!nearest) return;
    const npc = this.targets.find((t) => t.id === nearest.def.id);
    npc?.eatAndSnort();
    this.onFed?.(nearest.def.id);
  }

  /** Nakarm konkretnego konia z ręki (ObjectLayer give). */
  feedById(horseId: string): boolean {
    const npc = this.targets.find((t) => t.id === horseId);
    if (!npc) return false;
    void npc.playAction('eat');
    return true;
  }

  /** Czesanie najbliższego. */
  brushNearest(wx: number, wy: number): void {
    const nearest = this.nearest(wx, wy);
    if (!nearest) return;
    const npc = this.targets.find((t) => t.id === nearest.def.id);
    npc?.brush();
  }

  private nearest(wx: number, wy: number): HorseVisual | null {
    let best: HorseVisual | null = null;
    let bestD = Infinity;
    for (const h of this.horses) {
      const d = Math.hypot(h.state.x - wx, h.state.y - wy);
      if (d < bestD) {
        bestD = d;
        best = h;
      }
    }
    return best;
  }

  private pet(horse: HorseVisual): void {
    horse.state = petNpc(horse.state);
    playFx(this.scene, 'hearts', horse.state.x, horse.state.y - 48);
    this.onPetApi?.(horse.state.id);
  }

  update(dtSec: number): void {
    for (let i = 0; i < this.horses.length; i++) {
      const h = this.horses[i]!;
      h.state = tickNpc(h.state, dtSec, this.bounds);
      h.root.setPosition(h.state.x, h.state.y);
      h.root.setDepth(h.state.y + 2);

      // idle/walk placeholder animacja
      if (h.pose === 'eat') {
        /* tween handled in eatAndSnort */
      } else if (h.state.phase === 'wander') {
        h.walkPhase += dtSec * 7;
        h.pose = Math.floor(h.walkPhase) % 2 === 0 ? 'walkA' : 'walkB';
        const bob = Math.sin(h.walkPhase * Math.PI) * 3;
        h.body.setPosition(0, bob);
        h.head.setPosition(40, -10 + bob * 0.5);
        // „krok" nóg — scale Y wobble
        h.root.setScale(1, 1 + Math.sin(h.walkPhase * 2) * 0.03);
      } else {
        h.pose = 'idle';
        const bob = Math.sin(Date.now() / 400 + i) * 2;
        h.body.setPosition(0, bob);
        h.head.setPosition(40, -10 + bob * 0.4);
        h.root.setScale(h.state.phase === 'react' ? 1.08 : 1);
      }

      this.targets[i]?.syncFromVisual();
    }
  }

  destroy(): void {
    for (const h of this.horses) {
      h.root.destroy(true);
    }
    this.horses = [];
    this.targets = [];
  }
}
